import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Avatar,
  Chip,
  Button,
  IconButton,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Tooltip,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Radio,
  RadioGroup,
  FormLabel,
  Slider,
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  AccessTime as TimeIcon,
  CheckCircle as CheckIcon,
  DirectionsCar as CarIcon,
  LocalHospital as HospitalIcon,
  Assignment as AssignmentIcon,
  Description as PrescriptionIcon,
  ArrowForward as ArrowIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandIcon,
  MedicalServices as MedicalIcon,
  PlayArrow as PlayIcon,
  Close as CloseIcon,
  History as HistoryIcon,
  Medication as MedicationIcon,
  CalendarMonth as CalendarIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { RootState, AppDispatch } from '../../store';
import {
  fetchTodaysPendingAppointments,
  fetchTodaysAcceptedAppointments,
  fetchInProgressAppointments,
  acceptAppointment,
  startVisit,
  arriveAtLocation,
  completeExamination,
  clearWorkflowError,
} from '../../store/slices/appointmentSlice';

/**
 * Doctor Workflow Page
 * Complete workflow: Accept -> Start Visit -> Arrive -> Examination -> Prescription -> Close
 *
 * @author Civilhealth Development Team
 */

interface Appointment {
  id: number;
  bookingNumber: string;
  patientName: string;
  patientMobile: string;
  patientAge: number | null;
  patientGender: string | null;
  officerName: string;
  officerMobile: string;
  officerDesignation: string;
  officerDepartment: string;
  officerCadre?: string;
  appointmentDate: string;
  appointmentTime: string;
  appointmentType: string;
  status: string;
  statusDisplayName?: string;
  priority: string;
  symptoms: string;
  chiefComplaints: string;
  fullVisitAddress: string;
  visitCity: string;
  visitState: string;
  visitPincode: string;
  notes: string;
  doctorName: string;
  doctorSpecialization: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div hidden={value !== index}>
    {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
  </div>
);

const getStatusColor = (status: string): 'warning' | 'info' | 'primary' | 'success' | 'error' | 'default' => {
  switch (status) {
    case 'PENDING':
      return 'warning';
    case 'ACCEPTED':
      return 'info';
    case 'IN_PROGRESS':
      return 'primary';
    case 'VISITED':
      return 'info';
    case 'CLOSED':
      return 'success';
    case 'CANCELLED':
    case 'REJECTED':
      return 'error';
    default:
      return 'default';
  }
};

const getStatusLabel = (status: string): string => {
  switch (status) {
    case 'PENDING':
      return 'Pending';
    case 'ACCEPTED':
      return 'Accepted';
    case 'IN_PROGRESS':
      return 'In Progress';
    case 'VISITED':
      return 'Visited';
    case 'CLOSED':
      return 'Closed';
    case 'CANCELLED':
      return 'Cancelled';
    case 'REJECTED':
      return 'Rejected';
    default:
      return status;
  }
};

// Workflow Steps - New Flow: PENDING -> ACCEPTED -> IN_PROGRESS -> VISITED -> CLOSED
const WORKFLOW_STEPS = [
  { label: 'Pending', description: 'Appointment receive hui' },
  { label: 'Accepted', description: 'Doctor ne accept ki' },
  { label: 'In Progress', description: 'Doctor visit ke liye nikal gaya' },
  { label: 'Visited', description: 'Checkup complete' },
  { label: 'Closed', description: 'Report submit' },
];

const DoctorWorkflowPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const [tabValue, setTabValue] = useState(0);
  const [actionDialog, setActionDialog] = useState<{
    open: boolean;
    type: 'accept' | 'start' | 'arrive' | 'complete';
    appointment: Appointment | null;
  }>({ open: false, type: 'accept', appointment: null });
  const [detailsDialog, setDetailsDialog] = useState<{ open: boolean; appointment: Appointment | null }>({
    open: false,
    appointment: null,
  });
  const [notes, setNotes] = useState('');

  const { appointments, loading, workflowLoading, workflowError } = useSelector(
    (state: RootState) => state.appointment
  );
  const { user, isAuthenticated } = useSelector((state: RootState) => state.auth);

  // Redirect if not authenticated or not a doctor
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
      return;
    }
    if (user && user.role !== 'GOVERNMENT_DOCTOR') {
      navigate('/', { replace: true });
      return;
    }
  }, [isAuthenticated, user, navigate]);

  useEffect(() => {
    loadAppointments();
  }, [tabValue]);

  const loadAppointments = () => {
    switch (tabValue) {
      case 0:
        dispatch(fetchTodaysPendingAppointments());
        break;
      case 1:
        dispatch(fetchTodaysAcceptedAppointments());
        break;
      case 2:
        dispatch(fetchInProgressAppointments());
        break;
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    dispatch(clearWorkflowError());
  };

  const handleAction = async () => {
    if (!actionDialog.appointment) return;

    const appointmentId = actionDialog.appointment.id;

    try {
      switch (actionDialog.type) {
        case 'accept':
          await dispatch(acceptAppointment(appointmentId)).unwrap();
          break;
        case 'start':
          await dispatch(startVisit({ appointmentId, notes })).unwrap();
          break;
        case 'arrive':
          await dispatch(arriveAtLocation({ appointmentId })).unwrap();
          break;
        case 'complete':
          await dispatch(completeExamination({ appointmentId, notes })).unwrap();
          break;
      }
      setActionDialog({ open: false, type: 'accept', appointment: null });
      setNotes('');
      loadAppointments();
    } catch (error) {
      // Error is handled by Redux
    }
  };

  const openActionDialog = (type: 'accept' | 'start' | 'arrive' | 'complete', appointment: Appointment) => {
    setActionDialog({ open: true, type, appointment });
    setNotes('');
  };

  const closeActionDialog = () => {
    setActionDialog({ open: false, type: 'accept', appointment: null });
    setNotes('');
    dispatch(clearWorkflowError());
  };

  const getDialogTitle = () => {
    switch (actionDialog.type) {
      case 'accept':
        return 'Accept Appointment';
      case 'start':
        return 'Start Visit - Doctor On The Way';
      case 'arrive':
        return 'Mark Arrival - Start Examination';
      case 'complete':
        return 'Complete Examination - Close Case';
    }
  };

  const getDialogDescription = () => {
    switch (actionDialog.type) {
      case 'accept':
        return 'You are about to accept this appointment. The patient will be notified that you have accepted the case.';
      case 'start':
        return 'You are starting the visit. The patient will be notified that you are on the way to their location.';
      case 'arrive':
        return 'You have arrived at the patient location. The examination will begin now.';
      case 'complete':
        return 'Complete the examination and close this case. Make sure you have filled the Visit Report and uploaded any prescriptions.';
    }
  };

  const renderAppointmentCard = (appointment: Appointment, actionType?: 'accept' | 'start' | 'arrive' | 'complete') => (
    <Card key={appointment.id} sx={{ mb: 2, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
      <CardContent>
        <Grid container spacing={2}>
          {/* Patient Info */}
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              {/* HIGH CONTRAST: Light background + Dark icon */}
              <Avatar sx={{ bgcolor: 'transparent' , color: '#1D4ED8', mr: 1.5, width: 48, height: 48, border: '2px solid rgba(29, 78, 216, 0.2)' }}>
                <PersonIcon />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {appointment.patientName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {appointment.patientAge ? `${appointment.patientAge} years` : ''} {appointment.patientGender || ''}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                <PhoneIcon sx={{ fontSize: 16, mr: 1 }} />
                {appointment.patientMobile || 'N/A'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                <TimeIcon sx={{ fontSize: 16, mr: 1 }} />
                {appointment.appointmentTime} | {appointment.appointmentType?.replace('_', ' ')}
              </Typography>
            </Box>
          </Grid>

          {/* Officer Info */}
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Officer Details
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {appointment.officerName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {appointment.officerDesignation}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {appointment.officerDepartment} | {appointment.officerCadre}
            </Typography>
          </Grid>

          {/* Medical Info & Actions */}
          <Grid item xs={12} md={4}>
            <Box sx={{ mb: 1 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Chief Complaints
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {appointment.chiefComplaints || appointment.symptoms || 'Not specified'}
              </Typography>
            </Box>

            <Box sx={{ mb: 1 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Visit Address
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <LocationIcon sx={{ fontSize: 16, mr: 0.5, mt: 0.3 }} />
                {appointment.fullVisitAddress || `${appointment.visitCity}, ${appointment.visitState}`}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                label={getStatusLabel(appointment.status)}
                size="small"
                color={getStatusColor(appointment.status)}
              />
              {appointment.priority === 'EMERGENCY' || appointment.priority === 'URGENT' ? (
                <Chip label="Emergency" size="small" color="error" variant="outlined" icon={<WarningIcon />} />
              ) : null}
            </Box>

            <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<ViewIcon />}
                onClick={() => setDetailsDialog({ open: true, appointment })}
              >
                View Details
              </Button>
              {actionType && (
                <Button
                  size="small"
                  variant="contained"
                  startIcon={actionType === 'accept' ? <CheckIcon /> : actionType === 'start' ? <CarIcon /> : actionType === 'arrive' ? <LocationIcon /> : <CloseIcon />}
                  onClick={() => openActionDialog(actionType, appointment)}
                  disabled={workflowLoading}
                >
                  {actionType === 'accept' && 'Accept'}
                  {actionType === 'start' && 'Start Visit'}
                  {actionType === 'arrive' && 'Arrive'}
                  {actionType === 'complete' && 'Complete'}
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ p: 2, flex: 1, overflow: 'auto', bgcolor: 'transparent' }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#202124' }}>
            Visit Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your appointments from acceptance to completion
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadAppointments}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {/* Workflow Steps */}
      <Paper sx={{ mb: 3, p: 2, borderRadius: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
          Appointment Status Flow
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {WORKFLOW_STEPS.map((step, index) => (
            <Box key={step.label} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
              <Avatar
                sx={{
                  bgcolor: index <= tabValue ? '#1a73e8' : '#e0e0e0',
                  color: index <= tabValue ? 'white' : '#9e9e9e',
                  mb: 0.5,
                }}
              >
                {index + 1}
              </Avatar>
              <Typography variant="caption" sx={{ fontWeight: 600, textAlign: 'center' }}>
                {step.label}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', fontSize: '0.7rem' }}>
                {step.description}
              </Typography>
            </Box>
          ))}
        </Box>
      </Paper>

      {/* Tabs */}
      <Paper sx={{ borderRadius: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label={`Pending (${appointments?.length || 0})`} />
          <Tab label="Accepted" />
          <Tab label="In Progress" />
        </Tabs>

        {loading && <LinearProgress />}

        {workflowError && (
          <Alert severity="error" sx={{ m: 2 }} onClose={() => dispatch(clearWorkflowError())}>
            {workflowError}
          </Alert>
        )}

        <Box sx={{ p: 2 }}>
          {/* Pending Visits */}
          <TabPanel value={tabValue} index={0}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
              Today's Pending Visits (Awaiting Acceptance)
            </Typography>
            {appointments && appointments.length > 0 ? (
              (appointments as Appointment[]).map((apt) => renderAppointmentCard(apt, 'accept'))
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <ScheduleIcon sx={{ fontSize: 64, color: '#dadce0', mb: 2 }} />
                <Typography color="text.secondary">No pending visits</Typography>
              </Box>
            )}
          </TabPanel>

          {/* Accepted Visits */}
          <TabPanel value={tabValue} index={1}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
              Accepted Visits (Ready to Start)
            </Typography>
            {appointments && appointments.length > 0 ? (
              (appointments as Appointment[]).map((apt) => renderAppointmentCard(apt, 'start'))
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CheckIcon sx={{ fontSize: 64, color: '#dadce0', mb: 2 }} />
                <Typography color="text.secondary">No accepted visits</Typography>
              </Box>
            )}
          </TabPanel>

          {/* In Progress Visits */}
          <TabPanel value={tabValue} index={2}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
              Currently In Progress
            </Typography>
            {appointments && appointments.length > 0 ? (
              (appointments as Appointment[]).map((apt) =>
                renderAppointmentCard(
                  apt,
                  apt.status === 'IN_PROGRESS' ? 'arrive' : 'complete'
                )
              )
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <HospitalIcon sx={{ fontSize: 64, color: '#dadce0', mb: 2 }} />
                <Typography color="text.secondary">No in-progress visits</Typography>
              </Box>
            )}
          </TabPanel>
        </Box>
      </Paper>

      {/* Action Dialog */}
      <Dialog open={actionDialog.open} onClose={closeActionDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{getDialogTitle()}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {getDialogDescription()}
          </Typography>

          {actionDialog.appointment && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2">
                {actionDialog.appointment.patientName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {actionDialog.appointment.appointmentTime} | {actionDialog.appointment.bookingNumber}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {actionDialog.appointment.chiefComplaints || 'No complaints specified'}
              </Typography>
            </Paper>
          )}

          {(actionDialog.type === 'start' || actionDialog.type === 'complete') && (
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Notes (Optional)"
              placeholder="Add any notes about the visit..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              sx={{ mt: 1 }}
            />
          )}

          {actionDialog.type === 'complete' && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Please ensure you have filled the Visit Report and uploaded prescriptions before closing the case.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeActionDialog} disabled={workflowLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAction}
            disabled={workflowLoading}
            startIcon={workflowLoading ? <CircularProgress size={20} /> : null}
          >
            {workflowLoading ? 'Processing...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Patient Details Dialog */}
      <Dialog open={detailsDialog.open} onClose={() => setDetailsDialog({ open: false, appointment: null })} maxWidth="md" fullWidth>
        <DialogTitle>
          Appointment Details
          <IconButton
            onClick={() => setDetailsDialog({ open: false, appointment: null })}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {detailsDialog.appointment && (
            <Grid container spacing={3}>
              {/* Patient Information */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Patient Information
                </Typography>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <List dense>
                    <ListItem>
                      <ListItemText primary="Patient Name" secondary={detailsDialog.appointment.patientName} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Contact" secondary={detailsDialog.appointment.patientMobile} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Age/Gender" secondary={`${detailsDialog.appointment.patientAge || 'N/A'} / ${detailsDialog.appointment.patientGender || 'N/A'}`} />
                    </ListItem>
                  </List>
                </Paper>
              </Grid>

              {/* Officer Information */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Officer Information
                </Typography>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <List dense>
                    <ListItem>
                      <ListItemText primary="Officer Name" secondary={detailsDialog.appointment.officerName} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Designation" secondary={detailsDialog.appointment.officerDesignation} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Department" secondary={detailsDialog.appointment.officerDepartment} />
                    </ListItem>
                  </List>
                </Paper>
              </Grid>

              {/* Visit Details */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Visit Details
                </Typography>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <List dense>
                        <ListItem>
                          <ListItemText primary="Appointment Time" secondary={detailsDialog.appointment.appointmentTime} />
                        </ListItem>
                        <ListItem>
                          <ListItemText primary="Type" secondary={detailsDialog.appointment.appointmentType?.replace('_', ' ')} />
                        </ListItem>
                        <ListItem>
                          <ListItemText primary="Status" secondary={getStatusLabel(detailsDialog.appointment.status)} />
                        </ListItem>
                      </List>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <List dense>
                        <ListItem>
                          <ListItemText primary="Chief Complaints" secondary={detailsDialog.appointment.chiefComplaints || 'N/A'} />
                        </ListItem>
                        <ListItem>
                          <ListItemText primary="Symptoms" secondary={detailsDialog.appointment.symptoms || 'N/A'} />
                        </ListItem>
                        <ListItem>
                          <ListItemText primary="Notes" secondary={detailsDialog.appointment.notes || 'N/A'} />
                        </ListItem>
                      </List>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* Visit Address */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Visit Address
                </Typography>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="body1">
                    {detailsDialog.appointment.fullVisitAddress || 'Address not specified'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {detailsDialog.appointment.visitCity}, {detailsDialog.appointment.visitState} - {detailsDialog.appointment.visitPincode}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialog({ open: false, appointment: null })}>Close</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (detailsDialog.appointment) {
                navigate(`/visit-reports/create/${detailsDialog.appointment.id}`);
              }
            }}
          >
            Create Visit Report
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DoctorWorkflowPage;